<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import HeatmapChart from 'shared/components/charts/HeatmapChart.vue';

const { t } = useI18n();

const props = defineProps({
  grid: {
    type: Array,
    default: () => Array.from({ length: 7 }, () => Array(24).fill(0)),
  },
});

const WEEKDAYS = computed(() => [
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.SUN'),
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.MON'),
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.TUE'),
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.WED'),
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.THU'),
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.FRI'),
  t('FLOW_BUILDER.REPORTS.HEATMAP.WEEKDAYS.SAT'),
]);
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const maxValue = computed(() => {
  let max = 0;
  props.grid.forEach(row => {
    row.forEach(v => {
      if (v > max) max = v;
    });
  });
  return max;
});

function colorFor(value) {
  if (!value) return 'rgba(148, 163, 184, 0.15)';
  const ratio = Math.min(1, value / (maxValue.value || 1));
  const alpha = 0.2 + ratio * 0.7;
  return `rgba(59, 130, 246, ${alpha.toFixed(2)})`;
}

const dataset = computed(() => {
  const data = [];
  props.grid.forEach((row, day) => {
    row.forEach((value, hour) => {
      data.push({ x: String(hour), y: WEEKDAYS.value[day], v: value });
    });
  });
  return data;
});

const collection = computed(() => ({
  datasets: [
    {
      label: t('FLOW_BUILDER.REPORTS.HEATMAP.DISPATCHES'),
      data: dataset.value,
      backgroundColor: ctx => colorFor(ctx.raw?.v),
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      width: ({ chart }) => (chart.chartArea?.width || 0) / 24 - 2,
      height: ({ chart }) => (chart.chartArea?.height || 0) / 7 - 2,
    },
  ],
}));

const chartOptions = computed(() => ({
  plugins: {
    tooltip: {
      callbacks: {
        title: items => `${items[0].raw.y} · ${items[0].raw.x}h`,
        label: ctx => `${ctx.raw.v} mensagens`,
      },
    },
  },
  scales: {
    x: {
      type: 'category',
      labels: HOURS.map(String),
      ticks: {
        callback: value => (Number(value) % 3 === 0 ? `${value}h` : ''),
      },
      grid: { display: false },
    },
    y: {
      type: 'category',
      labels: [...WEEKDAYS.value].reverse(),
      offset: true,
      grid: { display: false },
    },
  },
}));
</script>

<template>
  <div class="h-56">
    <HeatmapChart :collection="collection" :chart-options="chartOptions" />
  </div>
</template>
